import axios from "axios";
import { API_URL } from "../_config/config.const";
import { handleResponse } from "../_helpers/handleResponse.helper";
import { getHeader, getUploadHeader } from "../_helpers/header.helper";
import { getQuery } from "../_helpers/query.helper";
import { tokenService } from "./token.service";

const API_NAME = "fileUpload";

export type UploadedFileResponse = {
    id: number;
    url?: string;
    name?: string;
    linkId?: number;
};

export const fileUploadService = {
    get,
    getAll,
    getAllWithBulkUpload,
    getAllFiles,
    uploadFile,
    disable,
    download,

    uploadForSpecialist,
    removeForSpecialist,
};

// ------- utility to ensure browser sets boundary for FormData -------
function stripContentType<T extends Record<string, any> | undefined>(headers: T): T {
    if (!headers) return headers;
    const copy: any = { ...headers };
    delete copy["Content-Type"];
    delete copy["content-type"];
    return copy;
}

async function get(value: number, query?: any) {
    const requestOptions = { method: "GET", headers: getHeader() };
    return await fetch(
        `${API_URL}/${API_NAME}/get/?id=${value}&${getQuery(query)}`,
        requestOptions
    ).then(handleResponse);
}

async function getAll(fileTypeId: number, id?: any) {
    const requestOptions = { method: "GET", headers: getHeader() };
    return await fetch(
        `${API_URL}/${API_NAME}/getAll/?fileTypeId=${fileTypeId}&${getQuery({id})}`,
        requestOptions
    ).then(handleResponse);
}

async function getAllWithBulkUpload(fileTypeId: number, query?: any) {
    const requestOptions = { method: "GET", headers: getHeader() };
    return await fetch(
        `${API_URL}/${API_NAME}/getAllWithBulkUpload/?fileTypeId=${fileTypeId}&${getQuery(query)}`,
        requestOptions
    ).then(handleResponse);
}

function getAllFiles(ids: Array<number>) {
    const _ids = ids.join("&ids=");
    const requestOptions = { method: "GET", headers: getHeader() };
    return fetch(
        `${API_URL}/${API_NAME}/GetAllFiles/?ids=${_ids}`,
        requestOptions
    ).then(handleResponse);
}

function uploadFile(model?: FormData) {
    const headers: any = stripContentType(getUploadHeader());
    return axios.post(`${API_URL}/${API_NAME}/UploadFile`, model, { headers });
}

function disable(id: any) {
    const requestOptions = { method: "DELETE", headers: getHeader() };
    return fetch(
        `${API_URL}/${API_NAME}/disable/?uploadFileId=${id}`,
        requestOptions
    ).then(handleResponse);
}

function download(id: any,fileType : number) {
    tokenService.GetReportToken().then((data) => {
        return window.open(`${API_URL}/${API_NAME}/GetFile/?token=${data}&uploadFileId=${id}&fileTypeId=${fileType}`, "_blank");
    });
}

// =================== Specialist-specific ===================

/**
 * Upload a document for a Specialist with a specific file type.
 * (server route: POST /api/fileUpload/specialist/{specialistId}/upload)
 *
 * @param specialistId number
 * @param file File
 * @param fileTypeId number (enum value on server)
 * @param meta optional { trainingEvent, trainingDate(ISO) }
 *
 * Returns: { id, url, name, linkId }
 */
async function uploadForSpecialist(
    specialistId: number,
    file: File,
    fileTypeId: number,
    meta?: { trainingEvent?: string; trainingDate?: string }
): Promise<UploadedFileResponse> {
    const form = new FormData();
    form.append("file", file, file.name);
    form.append("fileTypeId", String(fileTypeId)); // <-- ensure this matches server param
    if (meta?.trainingEvent) form.append("trainingEvent", meta.trainingEvent);
    if (meta?.trainingDate) form.append("trainingDate", meta.trainingDate);

    // Use auth headers but strip any Content-Type so the browser can set multipart boundary.
    const raw = getHeader(); // likely contains Authorization + maybe JSON content-type
    const headers: any = stripContentType(raw);

    const res = await fetch(
        `${API_URL}/${API_NAME}/specialist/${specialistId}/upload`,
        {
            method: "POST",
            headers,
            body: form,
        } as RequestInit
    );

    return handleResponse(res);
}

/**
 * Remove/unlink a Specialist file (and optionally delete blob on server).
 * (server route: DELETE /api/fileUpload/specialist/{specialistId}/remove?fileUploadId=###)
 */
async function removeForSpecialist(
    specialistId: number,
    fileUploadId: number
) {
    const headers = getHeader();
    const res = await fetch(
        `${API_URL}/${API_NAME}/specialist/${specialistId}/remove?fileUploadId=${fileUploadId}`,
        { method: "DELETE", headers }
    );

    return handleResponse(res);
}
